import React, { useEffect, useState } from 'react';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RichTextEditor } from './RichTextEditor';
import { MemberPicker } from './MemberPicker';
import { resourcesAPI } from '../lib/api';

// initial: { tipo, titulo, html, categoria, url, texto, items, source_id, user_ids }
export const ResourceAssignDialog = ({ initial, onClose, onDone }) => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(initial ? { user_ids: [], categoria: '', ...initial } : null); }, [initial]);
  if (!f) return null;
  const isDoc = f.tipo === 'doc';
  const upload = async (file) => { try { return (await resourcesAPI.uploadDocImage(file)).url; } catch (e) { toast.error(e.message); return null; } };

  const save = async () => {
    if (!f.titulo?.trim()) { toast.error('Pon un título'); return; }
    if (!f.user_ids.length) { toast.error('Elige al menos una persona'); return; }
    setSaving(true);
    try {
      const { items } = await resourcesAPI.assign(f);
      toast.success(`Guardado en los recursos de ${items.length} persona${items.length > 1 ? 's' : ''}`);
      onDone();
    } catch (e) { toast.error(e.message || 'No se pudo guardar'); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className={`${isDoc ? 'max-w-4xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`} data-testid="resource-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" />Guardar en los recursos de…</DialogTitle>
          <DialogDescription>Cada persona recibe su propia copia, independiente del original: si después cambias uno, los demás no cambian.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Personas</Label>
            <div className="mt-1.5"><MemberPicker value={f.user_ids} onChange={(v) => setF({ ...f, user_ids: v })} testId="assign-members" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label>Título de la copia</Label>
              <Input value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} placeholder="Ej: Kit de Marca — Cliente X" data-testid="assign-title-input" />
            </div>
            <div>
              <Label>Categoría (opcional)</Label>
              <Input value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} placeholder="Ej: Kits de marca" data-testid="assign-category-input" />
            </div>
          </div>
          {isDoc && (
            <div>
              <Label>Contenido (rellénalo antes de guardar)</Label>
              <div className="mt-1.5"><RichTextEditor value={f.html || ''} onChange={(h) => setF((x) => ({ ...x, html: h }))} minHeight={320} sticky={false} onImageUpload={upload} testId="assign-editor" /></div>
            </div>
          )}
          {f.tipo === 'file' && <p className="text-sm text-muted-foreground">Se guardará una copia del archivo «{f.original_filename || f.titulo}».</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="assign-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}Guardar copias</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
